import {
  CallParticipantCapability,
  isCapableOf,
  Role,
} from "@ameelio/connect-call-client";
import { Call, CallParticipant, Facility } from "../api/graphql.ts";
import featureEnabled from "../lib/featureEnabled.ts";
import { Peer } from "./LiveCall/types/ConnectionInfo";

// Determines if provided role is a host
export const isHost = (role?: Role): boolean =>
  !!(role && role === Role.webinarHost);

// Determines if provided role is a student
export const isAttendee = (role?: Role): boolean =>
  !!(
    role &&
    (role === Role.webinarAttendee || role === Role.webinarIsolatedAttendee)
  );

// Determines whether a peer can moderate others. Uses the capability set when
// the multi-participant layout is enabled, and falls back to the legacy role
// otherwise (backwards compatibility with the legacy call interfaces).
export const peerCanModerate = (peer: {
  capabilities?: CallParticipantCapability[] | null;
  user: { role?: Role };
}): boolean =>
  featureEnabled("VIDEO_MULTI_PARTICIPANT_LAYOUT")
    ? isCapableOf(CallParticipantCapability.REMOTE_CONTROL, peer)
    : isHost(peer.user.role);

const byId = (a: Peer, b: Peer) => a.user.id.localeCompare(b.user.id);

// Sorts peers by moderator status (secondary sort on id), moderators first
// by default, but can optionally sort by everyone else first instead.
export const getPeersOrdered = (
  peers: Peer[],
  sortRole?: "host" | "student"
) => {
  const sortedModerators = peers.filter(peerCanModerate).sort(byId);
  const sortedOthers = peers.filter((p) => !peerCanModerate(p)).sort(byId);
  return sortRole && sortRole === "student"
    ? [...sortedOthers, ...sortedModerators]
    : [...sortedModerators, ...sortedOthers];
};

export type IsolatedCall = Pick<
  Call,
  | "id"
  | "url"
  | "token"
  | "features"
  | "startedAt"
  | "endedAt"
  | "callType"
  | "callStatus"
  | "title"
  | "unlimited"
> & {
  facility: Pick<Facility, "id" | "publicId" | "virtualBackgrounds" | "name">;
  participants: Pick<
    CallParticipant,
    "externalId" | "fullName" | "role" | "type"
  >[];
};
